import {Component, DestroyRef, inject, OnInit, ViewChild} from '@angular/core';
import {masterMenuFilterFieldMap as fieldMap} from '@emm/shared/formly/maps/master-menu-system-filter.config';
import * as R from 'ramda';
import {BehaviorSubject, finalize, Observable, of} from 'rxjs';
import {filter, map, switchMap, take,} from 'rxjs/operators';
import * as RA from 'ramda-adjunct';
import {GenericItemsTreeComponent} from '../generic-items-tree/generic-items-tree.component';
import {MmsRevoteFormComponent} from '../mms-revote-form/mms-revote-form.component';
import dayjs from 'dayjs';
import {MatMenuTrigger} from '@angular/material/menu';
import {MatDialog} from '@angular/material/dialog';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {ContextMenuAction} from '@emm/core/models/context-menu-action.model';
import {MasterMenuFacadeService} from '@emm/management/services/master-menu-facade.service';
import {AuthFacadeService} from '@emm/auth/services/auth-facade.service';
import {DialogService} from '@emm/shared/services/dialog.service';
import {NotificationService} from '@emm/shared/services/notification.service';
import {SearchFilter} from '@emm/core/models/search-filter';
import {AddRevotesDialogComponent} from '@emm/shared/dialogs/add-revotes-dialog/add-revotes-dialog.component';
import {Revotes} from '@emm/core/models/revotes.model';
import {UserService} from '@emm/core/services/user.service';
import {API_ERRORS} from '@emm/core/util/api-constants';

@Component({
    selector: 'emm-management-master-menu-system',
    templateUrl: './management-master-menu-system.component.html',
    styleUrls: ['./management-master-menu-system.component.scss'],
    standalone: false
})
export class ManagementMasterMenuSystemComponent implements OnInit {
    @ViewChild(GenericItemsTreeComponent) tree: GenericItemsTreeComponent;
    @ViewChild(MmsRevoteFormComponent) mmsRevoteFormComponent: MmsRevoteFormComponent;
    @ViewChild(MatMenuTrigger, {static: true}) matMenuTrigger: MatMenuTrigger;

    private readonly destroyRef: DestroyRef = inject(DestroyRef);

    centerLoading$ = this.masterMenuFacade.centerLoading$;
    rightLoading$ = this.masterMenuFacade.rightLoading$;
    allRevotes$ = new BehaviorSubject<any>(undefined);
    allRevotesAsGenericItems$ = this.masterMenuFacade.allRevotesAsGenericItems$;
    isReadOnly$ = this.authFacade.isReadOnly$;
    isReadOnly = this.userService.hasLoginRole();
    isSuperAdmin = this.userService.hasSuperAdminRole();
    isAdmin = this.userService.hasAdminRole();
    isAuthor = this.userService.hasAuthorRole();
    prevRevote: any;
    fields = [fieldMap['name']()];
    selectedConsumer = '';
    revotesCache = [];

    revoteContextMenuActions = [
        {
            // Delete
            enabled: (revote) => !revote.locked,
            click: (revote) => this.onDeleteItem(revote),
            visible: R.T,
            icon: R.always('delete'),
            text: R.always('Delete'),
        }
    ] as ContextMenuAction<any>[];

    constructor(
        private masterMenuFacade: MasterMenuFacadeService,
        private authFacade: AuthFacadeService,
        private dialogService: DialogService,
        private notificationService: NotificationService,
        private dialog: MatDialog,
        private userService: UserService) {
    }

    ngOnInit(): void {
        this.masterMenuFacade.init();
        this.getRevotes();
    }

    getRevotes() {
        this.masterMenuFacade.startProgressBar();
        this.allRevotesAsGenericItems$
            .pipe(
                finalize(() => this.masterMenuFacade.stopProgressBar()),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(result => {
            this.revotesCache = result;
            this.allRevotes$.next(this.revotesCache);
        });
    }

       onFilter(filter: SearchFilter): void {
        this.filterNodes(filter.value as string, filter.key);
    }


    filterNodes(value: string, selected: string): void {
        this.allRevotes$.next(this.revotesCache);
        this.allRevotesAsGenericItems$.pipe(
            take(1),
            map(list => {
                const filteredNodes = list.filter(nd => nd['name'].toLowerCase().includes(value.toLowerCase()));
                this.allRevotes$.next(filteredNodes);
            }),
            takeUntilDestroyed(this.destroyRef)
        ).subscribe();
    }

    openCreateRevoteDialog() {
        const dialogRef = this.dialog.open(AddRevotesDialogComponent, {
            width: '600px',
            data: {
                locations: this.masterMenuFacade.locations$,
                categories: this.masterMenuFacade.categories$,
            }
        });

        dialogRef.afterClosed()
            .pipe(
                filter((result) => result && result === 'submit'),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(() => {
            this.notificationService.success('Created successfully');
            this.getRevotes();
        });
    }

    onDeleteItem(selectedRevote) {
        const dialogRef = this.dialogService.openConfirmDialog('Are you sure you want to delete',
            selectedRevote.name, null);
        try {
            dialogRef.afterClosed()
                .pipe(
                    switchMap(result => result ? this.masterMenuFacade.api.deleteRevote(selectedRevote.id) : of(false)),
                    takeUntilDestroyed(this.destroyRef)
                )
                .subscribe({
                    next: (response) => {
                        if (response === false) {
                            this.notificationService.error(`Delete was cancelled`);
                        } else {
                            this.notificationService.success(`Successfully deleted ${selectedRevote.name}`);
                            this.getRevotes();
                        }
                    },
                    error: (error) => {
                        this.notificationService.error(error?.error?.error.message || API_ERRORS.GenericErrorMessage);
                    }
                });
        } catch (e) {
            this.notificationService.error(`Failed to delete ${selectedRevote.name}`);
        }
    }

    onSelectRevote(selected) {
        if (RA.isNotNil(selected)) {
            this.checkIfItemHasChanges(this.prevRevote)
                .pipe(take(1), takeUntilDestroyed(this.destroyRef))
                .subscribe((_) => {
                    this.prevRevote = selected['source'];
                    this.masterMenuFacade.selectRevote(selected['source']);
                });
        } else {
            this.prevRevote = null;
        }
    }

    chooseConsumer(selectedConsumer: string) {
        this.masterMenuFacade.updateLocations(selectedConsumer);
        this.masterMenuFacade.updateCategories(selectedConsumer);
    }

    openMenu(event: Event) {
        try {
            this.matMenuTrigger.openMenu();
        } catch (e) {}
    }

    onSelectStandard(): void {
        this.selectedConsumer = 'MENU_ALIGNMENT';
        this.chooseConsumer(this.selectedConsumer);
        this.openCreateRevoteDialog();
    }

    onSelectCaptive(): void {
        this.selectedConsumer = 'MMS_LICENSEE';
        this.chooseConsumer(this.selectedConsumer);
        this.openCreateRevoteDialog();
    }

    checkIfItemHasChanges(prevRevote: Revotes): Observable<boolean> {
        const today = dayjs(new Date()).format('YYYY-MM-DD');
        return this.isReadOnly$.pipe(
            take(1),
            switchMap((isReadOnly) => {
                if (this.hasChanges() && !isReadOnly && RA.isNotNil(prevRevote) &&
                    dayjs(this.prevRevote.openDate).format('YYYY-MM-DD') > today) {
                    return this.dialogService
                        .openSaveChangesDialog(prevRevote.name)
                        .afterClosed()
                        .pipe(
                            switchMap((res) => {
                                if (res) {
                                    this.mmsRevoteFormComponent.submit(prevRevote, false);
                                    return this.masterMenuFacade.updateRevote$ as Observable<boolean>;
                                } else {
                                    this.mmsRevoteFormComponent.cancel();
                                    return of(true);
                                }
                            })
                        );
                }

                return of(true);
            })
        );
    }

    hasChanges(): boolean {
        return this.mmsRevoteFormComponent.hasChanges();
    }
}
